import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";

const AuthCallback: React.FC = () => {
  const location = useLocation();
  const [message, setMessage] = useState<string>("Completing sign-in...");
  const didRun = useRef(false);
  const navigated = useRef(false);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  // Read flow from localStorage (set before OAuth redirect) or fall back to URL param
  const redirect = useMemo(() => {
    const fromUrl = params.get("redirect");
    if (fromUrl) return fromUrl;
    try {
      // Arrived from a Shopify install before signing in: finish connecting that store first.
      if (localStorage.getItem('flowstock.pendingShopifyInstall')) return '/onboarding';
      const flow = localStorage.getItem('flowstock.auth.flow');
      return flow === 'provider' ? '/provider/catalog' : '/dashboard';
    } catch {
      return '/dashboard';
    }
  }, [params]);
  const code = params.get("code");
  const error = params.get("error");

  // Helper to navigate only once
  const go = (path: string) => {
    if (navigated.current) return;
    navigated.current = true;
    window.location.replace(path);
  };

  useEffect(() => {
    if (didRun.current) return; // guard against StrictMode double-invoke
    didRun.current = true;

    // Subscribe to auth changes; navigate on SIGNED_IN as a robust signal
    const { data: listener } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "SIGNED_IN" && sess) {
        go(redirect);
      }
    });

    const finalize = async () => {
      try {
        if (error) {
          setMessage(`Sign-in error: ${error}`);
          setTimeout(() => go("/login"), 1200);
          return;
        }

        // If we already have a session, just go
        const { data: existing } = await supabase.auth.getSession();
        if (existing?.session) {
          go(redirect);
          return;
        }

        // If code is present in search, complete the exchange explicitly
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );
          if (exErr) {
            setMessage(`Authentication failed: ${exErr.message}`);
            setTimeout(() => go("/login"), 1500);
            return;
          }
          go(redirect);
          return;
        }

        // No code present: just go to login
        go("/login");
      } catch (e: any) {
        console.error("Auth callback error:", e);
        setMessage("Unexpected error during authentication.");
        setTimeout(() => go("/login"), 1500);
      }
    };

    // As an ultimate fallback, force navigation after a short delay.
    const hardFallback = setTimeout(() => {
      if (!navigated.current) {
        go(redirect);
      }
    }, 800);

    finalize();

    return () => {
      listener.subscription.unsubscribe();
      // Clear fallback timer if any
      try {
        clearTimeout(hardFallback as any);
      } catch {
        /* noop */
      }
    };
  }, [code, error, redirect]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 font-mono text-sm">{message}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
